import { useRef, useState, useMemo, useCallback } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';

import {
  Popover,
  TouchDialog,
  Icon,
  registerIcon,
  useOuterEvent,
  Menu,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  calculateFontSize,
  isInstance,
  isTouchDevice
} from '@pega/cosmos-react-core';
import type { TranslationFunction, TranslationPack } from '@pega/cosmos-react-core';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';

import { getKeyCommand } from '../../RichTextEditor/Toolbar/utils';
import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(arrowMicroDownIcon);

const StyledTextSelect = styled(Button)(({ theme }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    border-radius: calc(0.25 * ${theme.base['border-radius']});
    height: calc(4 * ${theme.base.spacing});
    padding: calc(0.5 * ${theme.base.spacing});
    font-size: ${fontSizes.xxs};

    span {
      white-space: nowrap;
    }

    :focus {
      box-shadow: ${theme.base.shadow.focus};
    }
  `;
});

StyledTextSelect.defaultProps = defaultThemeProp;

const StyledSelectMenu = styled.div`
  min-width: 20ch;
`;

StyledSelectMenu.defaultProps = defaultThemeProp;

const formatMapping = {
  p: 'normal',
  li: 'normal',
  ul: 'normal',
  ol: 'normal',
  div: 'normal',
  h1: 'heading-1',
  h2: 'heading-2',
  h3: 'heading-3',
  h4: 'heading-4',
  '': 'normal'
} as const;

type FormatType = keyof typeof formatMapping;
type Format = (typeof formatMapping)[FormatType];

const getTextCommand = (num: number, osx: boolean) => {
  return getKeyCommand(osx, ({ ctrl, alt }) => `${ctrl}${alt}${num}`);
};

export const getTextFormats = (
  t: TranslationFunction<TranslationPack>,
  osx: boolean = false
): { id: Format; text: string; subText: string; type: FormatType }[] => {
  return [
    {
      id: 'normal',
      text: t('rte_heading_style_normal'),
      subText: getTextCommand(0, osx),
      type: 'p'
    },
    {
      id: 'heading-1',
      text: t('rte_heading_style_heading_1'),
      subText: getTextCommand(1, osx),
      type: 'h1'
    },
    {
      id: 'heading-2',
      text: t('rte_heading_style_heading_2'),
      subText: getTextCommand(2, osx),
      type: 'h2'
    },
    {
      id: 'heading-3',
      text: t('rte_heading_style_heading_3'),
      subText: getTextCommand(3, osx),
      type: 'h3'
    },
    {
      id: 'heading-4',
      text: t('rte_heading_style_heading_4'),
      subText: getTextCommand(4, osx),
      type: 'h4'
    }
  ];
};

const TextSelect = ({ osx, editor, ...restProps }: { osx: boolean; editor: TiptapEditor }) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const selecting = useRef(false);
  const openedByClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);

  const textFormats = useMemo(() => {
    return getTextFormats(t, osx);
  }, [t, osx]);

  useOuterEvent('mousedown', [buttonRef, popoverRef, menuRef, touchDialogRef], () => {
    setOpen(false);
  });

  const handleEscapeClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const onTextSelect = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Tiptap API
    const chain = editor.chain().focus();
    if (id === 'p') {
      chain.setParagraph().run();
    } else if (id === 'h1') {
      chain.setHeading({ level: 1 }).run();
    } else if (id === 'h2') {
      chain.setHeading({ level: 2 }).run();
    } else if (id === 'h3') {
      chain.setHeading({ level: 3 }).run();
    } else if (id === 'h4') {
      chain.setHeading({ level: 4 }).run();
    }
    if (!openedByClick.current) {
      buttonRef.current?.focus();
    }
    setOpen(false);
    selecting.current = true;
    setTimeout(() => {
      selecting.current = false;
      if (!editor.isFocused && openedByClick.current) {
        editor.commands.focus();
      }
    }, 0);
  };

  const activeFormat = (): FormatType | undefined => {
    // Tiptap API
    if (editor.isActive('heading', { level: 1 })) return 'h1';
    if (editor.isActive('heading', { level: 2 })) return 'h2';
    if (editor.isActive('heading', { level: 3 })) return 'h3';
    if (editor.isActive('heading', { level: 4 })) return 'h4';
    if (editor.isActive('paragraph')) return 'p';
    return 'p';
  };

  const formatText = (() => {
    const activeFormatType = activeFormat();

    const unknown = t('rte_heading_style_unknown');

    if (!activeFormatType) return unknown;

    const format = formatMapping[activeFormatType];

    if (!format) return unknown;

    return textFormats.find(({ id }) => id === format)?.text ?? unknown;
  })();

  const menuContent = (
    <Menu
      items={textFormats.map(({ text, type, subText }) => ({
        id: type,
        primary: text,
        secondary: [subText],
        selected: type === activeFormat()
      }))}
      focusControlEl={isTouchDevice ? undefined : buttonRef.current ?? undefined}
      mode='single-select'
      ref={menuRef}
      onItemClick={onTextSelect}
    />
  );

  return (
    <>
      <StyledTextSelect
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          if (!open) {
            openedByClick.current = true;
          }
          setOpen(!open);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !selecting.current) {
            e.preventDefault();
            setOpen(true);
            openedByClick.current = false;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            selecting.current = false;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            setOpen(false);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          const { relatedTarget } = e;
          if (
            isInstance(relatedTarget, Node) &&
            !buttonRef.current?.contains(relatedTarget) &&
            !popoverRef.current?.contains(relatedTarget) &&
            !menuRef.current?.contains(relatedTarget) &&
            !touchDialogRef.current?.contains(relatedTarget)
          ) {
            setOpen(false);
          }
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-expanded={open}
        aria-label={`${t('rte_text_formatting_toolbar')}. ${t(
          'rte_heading_style'
        )}. ${formatText} ${t('selected').toLocaleLowerCase()}`}
        aria-haspopup
      >
        <span>{formatText}</span>
        <Icon name='arrow-micro-down' />
      </StyledTextSelect>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_heading_style')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={t('rte_heading_style')}
          onDismiss={() => setOpen(false)}
        >
          {menuContent}
        </TouchDialog>
      ) : (
        <Popover
          show={open}
          as={StyledSelectMenu}
          target={buttonRef.current}
          placement='bottom-start'
          ref={popoverRef}
        >
          {menuContent}
        </Popover>
      )}
    </>
  );
};

export default TextSelect;
